// 2026-03-01 (Dave) — Deleted elements drawer.
// Sliding side-panel that lists soft-deleted rows for the current
// association so users can restore without having to remember the
// exact name. Rows are sorted newest-deleted first (audit-derived)
// so the last accidental click is on top.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { X, Undo2, Loader2, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/formatters";
import ConfirmDialog from "./ConfirmDialog";
import { useAuth, isAdmin } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeletedElementsDrawer({ aid, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const canPurge = isAdmin(user);
  const [purgeTarget, setPurgeTarget] = useState(null); // { label, ids }
  const [purgeText, setPurgeText] = useState("");
  const askPurge = (label, els) => { setPurgeText(""); setPurgeTarget({ label, ids: els.map((e) => e.element_id) }); };

  const confirmPurge = async () => {
    const { ids, label } = purgeTarget;
    try {
      await axios.post(`${API}/associations/${aid}/elements/bulk-purge`, { element_ids: ids });
    } catch (e) {
      fetchList();
      throw e;
    }
    setItems((prev) => prev.filter((x) => !ids.includes(x.element_id)));
    setPurgeTarget(null);
    toast.success(`${label} permanently deleted`);
  };

  const PurgeBtn = ({ onClick, testId, title, label }) => (
    <button
      onClick={onClick}
      disabled={busyId !== null}
      className="flex items-center gap-1 px-1.5 py-0.5 text-[10.5px] font-semibold border border-coral text-coral rounded-[3px] hover:bg-coral hover:text-white disabled:opacity-40 transition-colors"
      data-testid={testId}
      title={title}
      aria-label={title}
    >
      <Trash2 size={10} />{label}
    </button>
  );

  const fetchList = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/associations/${aid}/elements/deleted`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load deleted elements.");
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const restore = async (el) => {
    setBusyId(el.element_id);
    try {
      const res = await axios.post(`${API}/elements/${el.element_id}/restore`);
      toast.success(`"${el.name}" restored`);
      // Optimistically remove from local list.
      setItems((prev) => prev.filter((x) => x.element_id !== el.element_id));
      if (onChanged) await onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };

  const restoreCategory = async (cat, els) => {
    setBusyId(`cat:${cat}`);
    try {
      const ids = els.map((e) => e.element_id);
      const res = await axios.post(`${API}/associations/${aid}/elements/bulk-restore`, { element_ids: ids });
      toast.success(`"${cat}" restored (${ids.length})`);
      setItems((prev) => prev.filter((x) => !ids.includes(x.element_id)));
      if (onChanged) await onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };

  const byCat = new Map();
  for (const el of items) {
    const c = el.category || "Uncategorized";
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c).push(el);
  }

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="deleted-elements-drawer">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/30"
        onClick={onClose}
        data-testid="deleted-elements-backdrop"
      />
      {/* Panel */}
      <aside
        className="w-[380px] max-w-[92vw] bg-white shadow-2xl border-l-2 border-navy flex flex-col"
        style={{ animation: "slideInRight 200ms ease-out" }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b-2"
          style={{ borderColor: "#EA6A47", background: "var(--u-navy-wash)" }}
        >
          <div className="flex items-center gap-2">
            <ArchiveRestore size={14} className="text-navy" />
            <div>
              <div className="text-[13px] font-semibold text-navy leading-none">Deleted Elements</div>
              <div className="text-[10.5px] text-slate mt-0.5">
                Click Restore to bring one back
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate hover:text-navy" data-testid="deleted-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-3 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="text-[12px] text-coral p-3 border border-coral/30 rounded-[4px] bg-coral/5">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate text-[12px]">
              Nothing to restore. Deleted elements will show up here.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
            {[...byCat.entries()].map(([cat, els]) => (
            <div key={cat} data-testid={`deleted-category-${cat}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-navy">
                  {cat} <span className="text-slate">({els.length})</span>
                </span>
                <div className="flex items-center gap-1">
                {canPurge && (
                  <PurgeBtn
                    onClick={() => askPurge(`Category "${cat}" (${els.length})`, els)}
                    testId={`purge-category-${cat}`}
                    title="Permanently delete every Element in this category"
                  />
                )}
                {els.length > 1 && (
                  <button
                    onClick={() => restoreCategory(cat, els)}
                    disabled={busyId !== null}
                    className="flex items-center gap-1 px-2 py-0.5 text-[10.5px] font-semibold border border-navy text-navy rounded-[3px] hover:bg-navy hover:text-white disabled:opacity-40 transition-colors"
                    data-testid={`restore-category-${cat}`}
                    title="Restore every Element in this category"
                  >
                    {busyId === `cat:${cat}` ? <Loader2 size={10} className="animate-spin" /> : <Undo2 size={10} />}
                    Restore Category
                  </button>
                )}
                </div>
              </div>
            <ul className="flex flex-col gap-1.5">
              {els.map((el) => (
                <li
                  key={el.element_id}
                  className="flex items-center gap-2 px-2 py-1.5 border border-rule rounded-[4px] hover:bg-navy-wash/40"
                  data-testid={`deleted-row-${el.element_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[11px] font-mono text-slate tabular-nums">
                        {el.element_display_id || "—"}
                      </span>
                      <span className="text-[12px] text-navy truncate">{el.name}</span>
                    </div>
                    <div className="text-[10px] text-slate mt-0.5">
                      Deleted {el.deleted_at ? fmtDateTime(el.deleted_at) : "—"}
                      {el.deleted_by_email && (
                        <> · by {el.deleted_by_email}</>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => restore(el)}
                    disabled={busyId === el.element_id}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-navy text-navy rounded-[3px] hover:bg-navy hover:text-white disabled:opacity-40 transition-colors"
                    data-testid={`restore-${el.element_id}`}
                    title="Restore this element to the plan"
                  >
                    {busyId === el.element_id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <Undo2 size={11} />}
                    Restore
                  </button>
                  {canPurge && (
                    <PurgeBtn
                      onClick={() => askPurge(`"${el.name}"`, [el])}
                      testId={`purge-${el.element_id}`}
                      title="Permanently delete this Element"
                    />
                  )}
                </li>
              ))}
            </ul>
            </div>
            ))}
            </div>
          )}
        </div>

        <div
          className="px-4 py-2 border-t border-rule text-[10.5px] text-slate flex flex-col gap-1.5"
          style={{ background: "var(--u-navy-wash)" }}
        >
          <span className="flex items-center justify-between gap-2 whitespace-nowrap">
            {items.length} deleted
            {canPurge && items.length > 0 && (
              <PurgeBtn
                onClick={() => askPurge(`All ${items.length} deleted Element${items.length === 1 ? "" : "s"}`, items)}
                testId="purge-all"
                title="Permanently delete everything in this drawer"
                label="Empty Drawer"
              />
            )}
          </span>
          <span className="italic">Restored elements land back in the grid with their original ID.</span>
        </div>
      </aside>
      <ConfirmDialog
        open={!!purgeTarget}
        title="Permanently delete?"
        message={
          purgeTarget ? (
            <>
              <span className="font-semibold">{purgeTarget.label}</span> will be erased for good, along with
              {purgeTarget.ids.length === 1 ? "its" : "their"} attachments. <span className="font-semibold text-coral">This cannot be undone.</span>
              <div className="mt-3 text-[12px] text-slate">
                Type <span className="font-semibold text-navy">DELETE</span> to confirm:
              </div>
              <input
                autoFocus
                value={purgeText}
                onChange={(ev) => setPurgeText(ev.target.value)}
                className="mt-1 w-full border border-rule rounded-[3px] px-2 py-1 text-[13px]"
                data-testid="purge-confirm-input"
              />
            </>
          ) : ""
        }
        confirmLabel="Delete Forever"
        tone="danger"
        confirmDisabled={purgeText.trim() !== "DELETE"}
        onConfirm={confirmPurge}
        onCancel={() => setPurgeTarget(null)}
        testid="purge-confirm"
      />
    </div>
  );
}
